/* eslint-disable no-nested-ternary */
import React, {
	useState,
	useEffect,
	useMemo,
	useRef,
	useCallback,
} from 'react';
import classNames from 'classnames';
import {
	batch,
	useDispatch,
	useSelector,
} from 'react-redux';
import {
	DndContext,
	DragOverlay,
	useSensor,
	useSensors,
	MouseSensor,
	DragStartEvent,
	DragEndEvent,
	DragOverEvent,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
} from '@dnd-kit/sortable';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { track } from '@amplitude/analytics-browser';
import { GreenroomState as GS } from '../../stores/types/types';
import {
	Shot,
	PLAY_STATES,
	baseShot,
	baseShotData,
	ShotData,
	baseShotFeed,
} from '../../stores/reducers/shots';
import { ROOM_STATES } from '../../stores/reducers/settings';
import Socket from '../../connection/socket';
import * as ShotsActions from '../../stores/actions/shots';
import * as TabsActions from '../../stores/actions/tabs';
import * as SettingsActions from '../../stores/actions/settings';
import * as ShowboardActions from '../../stores/actions/user-interface';
import { durationString } from '../../utils/utils';
import useShots from '../../contexts/shots/provider';
import usePrevious from '../../utils/use-previous';
import Icon, {
	ICONS,
	COLORS,
} from '../general-components/icon';
import ShowboardRowThumbnail from './showboard-row-thumbnail';
import DraggableShotThumbnail from './draggable-shot-thumbnail';
import ShowboardHeader from './showboard-header';
import LayoutModal from './layout-modal';
import BetweenShot from './between-shot';
import * as MediaActions from '../../stores/actions/media';
import { PresentedMediaItem } from '../../stores/reducers/media';
import { appLogger } from '../../utils/datadog';
import { useVideoMachine } from '../../contexts/video-machine';
import { isMovingForward } from '../../utils/custom-graphics';
import useAutoplay from '../../hooks/use-autoplay';
import {
	getPresentedAutoDurationMediaId,
	getPresentedAutoDurationTimeLeft,
} from '../../utils/media';
import { AmpEvent } from '../../types/amplitude';
import { useOpenShotModal } from '../../hooks/use-shot-utilities';
import {
	removeAllAnimatedOverlays,
	resetAllShotOverlays,
} from '../../connection/graphics';
import V_LAYOUTS from '../../utils/video-layout';
import { clearShotStartTimestampEntity } from '../../connection/shots';
import TooltipWrapper from '../general-components/tooltips/tooltip-wrapper';
import { TitlesProvider } from '../../contexts/titles/titles-context';
import { TABS } from '../../stores/types/tabs';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { virtualizedHorizontalSortingStrategy } from '../../utils/virtualized-horizontal-list-sorting-strategy';
import useMetaMenuDrawer from '../../hooks/use-meta-menu-drawer';

interface OwnProps {
	socket: Socket | null;
}

export default function ShowboardDrawer(props: OwnProps) {
	const dispatch = useDispatch();
	const { socket } = props;
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const shotStartTimestampEntity = useSelector(
		(s: GS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shotTabShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const clearShot = useSelector((s: GS) => s.SettingsReducer.clearShot);
	const isShowboardExpanded = useSelector((s: GS) => s.UserInterfaceReducer.isShowboardExpanded);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const animatedOverlays = useSelector(
		(s: GS) => (s.GraphicsReducer.animatedOverlays),
	);
	const activeTab = useSelector((s: GS) => s.TabsReducer.activeTab);

	const {
		handleSelectShot,
		currentShot,
		handleAddingShot,
		getMissingFeeds,
		setNextShotValue,
	} = useShots();

	const handleOpenShotModal = useOpenShotModal();
	const { closeMetaMenuAndDrawer } = useMetaMenuDrawer();

	const {
		shot_id: shotId,
		shot_data: shotData,
		shot_data: {
			shotDuration,
			shotFeeds,
			autoDuration,
			order,
			autoplay,
			videoTitlesOn
		},
	} = currentShot;

	const {
		videoMachineState: {
			timeLeft,
			isVideoSet,
		},
	} = useVideoMachine();

	const {
		setShotAutoplay,
		deleteShotAutoplay,
	} = useAutoplay();

	const prevRoomState = usePrevious(roomState);
	const prevShotStart = usePrevious(shotStartTimestampEntity);
	const prevShotId = usePrevious(shotId);
	const prevShotDuration = usePrevious(shotDuration);
	const prevAutoDuration = usePrevious(autoDuration);
	const sensors = useSensors(useSensor(MouseSensor));

	const shotRowRef = useRef<HTMLDivElement | null>(null);
	const playingTimeout = useRef(undefined as undefined | NodeJS.Timeout);
	const playShotRef = useRef<HTMLButtonElement | null>(null);
	const drawerRef = useRef<HTMLDivElement | null>(null);
	const prevAutoplayRef = useRef<{ shotId: number; autoplay: boolean } | undefined>(autoplay !== undefined ? { shotId: shotId, autoplay: autoplay } : undefined);
	const virtuosoRef = useRef<VirtuosoHandle | null>(null);
	const programmaticScrollRef = useRef(false);

	// when live as a producer, a playing shot will initially be pinned to the bottom left
	const [isScrollingPinned, setIsScrollingPinned] = useState<boolean>(false);
	// scroll position for unpinned state, percentage based on where the user last scroll to
	const [scrollPercent, setScrollPercent] = useState<number>(0);
	// current shot thumbnail's scroll position relative to it's pinned scroll position
	const [currentShotScrollPos, setCurrentShotScrollPos] = useState<number>(0);
	const [upcomingString, setUpcomingString] = useState<string>('');
	const [playState, setPlayState] = useState<number>(PLAY_STATES.PLAYING);
	const [draggingShot, setDraggingShot] = useState<Shot | null>(null);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const [stopAutoScroll, setStopAutoScroll] = useState<boolean>(false);
	const [scrollDisabled, setScrollDisabled] = useState<boolean>(false);
	const [shiftKeyDepressed, setShiftKeyDepressed] = useState<boolean>(false);
	const [addShotModalOpen, setAddShotModalOpen] = useState<boolean>(false);
	const [hoveringOnPlayButton, setHoveringOnPlayButton] = useState<boolean>(false);
	const [shotToSwitchTo, setShotToSwitchTo] = useState<number>(-1);
	const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
	const [isVirtuosoScrolling, setIsVirtuosoScrolling] = useState(false);

	const shotTabIsCurrentActiveShot = shotTabShotId === currentShotId;

	const pinnedScrollPos = useMemo(() => {
		const currentShotIndex = shots.findIndex((shot: Shot) => shot.shot_id === shotId);

		const safeIndex = currentShotIndex >= 0 ? currentShotIndex : shots.length - 1;

		setCurrentShotScrollPos(safeIndex);
		return safeIndex;
	}, [roomState, shotStartTimestampEntity, shots, shotId]);

	function scrollToPinnedShot(shotIndex: number, behavior: ScrollBehavior) {
		if (virtuosoRef.current && shotIndex > -1) {
			virtuosoRef.current.scrollToIndex({
				index: shotIndex,
				behavior: behavior as 'auto' | 'smooth',
				align: 'start',
				offset: 22,
			});
			setIsScrollingPinned(true);
			setScrollDisabled(false);
			setCurrentShotScrollPos(0);
		}
	}

	function handleAutoScroll() {
		const currentShotIndex = shots.findIndex((shot: Shot) => shot.shot_id === shotId);
		if (shotRowRef.current && currentShotIndex > -1) {
			// when changing from the unpinned scrolling state to the pinned scrolling state, we
			// need to wait for  the css transition to correctly pin the selected shot to the bottom left
			if (isScrollingPinned) {
				scrollToPinnedShot(currentShotIndex, 'smooth');
			} else {
				setIsScrollingPinned(true);
			}
		}
	}

	function checkShiftKey(e: KeyboardEvent): void {
		setShiftKeyDepressed(e.shiftKey);
	}

	function unsetShiftKey(): void {
		setShiftKeyDepressed(false);
	}

	function updateCoundownUI(countdown: number | undefined) {
		if (playingTimeout.current) {
			clearTimeout(playingTimeout.current);
		}

		if (countdown === undefined) {
			setUpcomingString('');
			setPlayState(PLAY_STATES.PLAYING);
			return;
		}

		setUpcomingString(durationString(Math.abs(countdown)));
		if (countdown > 5) {
			setPlayState(PLAY_STATES.PLAYING);
		} else if (countdown <= 0) {
			setPlayState(PLAY_STATES.LATE);
		} else {
			setPlayState(PLAY_STATES.ENDING);
		}
	}

	function playCountdown() {
		if (shotStartTimestampEntity) {
			const startTime = new Date(shotStartTimestampEntity.valueOf());
			const endTime = startTime.getTime() + (shotDuration * 1000);

			const nowish = Date.now();
			const countdownSync = (endTime - nowish) % 1000;
			const countdown = Math.round((endTime - nowish) / 1000);

			updateCoundownUI(countdown);
			playingTimeout.current = setTimeout(() => {
				playCountdown();
			}, countdown > 0 ? countdownSync : 1000 + countdownSync);
		}
	}

	const missingFeedsInNextShot = useMemo(() => {
		if (roomState === ROOM_STATES.EDITING) {
			return '';
		} if (!shotStartTimestampEntity) {
			return getMissingFeeds(shotFeeds);
		}
		const currentIndex = shots.findIndex((shot: Shot) => shot.shot_id === shotId);
		return shots[currentIndex + 1]
			? getMissingFeeds(shots[currentIndex + 1].shot_data.shotFeeds)
			: '';
	}, [currentShot, connectedUsers, shots, roomState, shotStartTimestampEntity]);

	useEffect(() => {
		window.addEventListener('keydown', checkShiftKey);
		window.addEventListener('keyup', checkShiftKey);

		// When the user is no longer focused on the window
		// or opens the context menu
		window.addEventListener('blur', unsetShiftKey);
		window.addEventListener('contextmenu', unsetShiftKey);

		return () => {
			window.removeEventListener('keydown', checkShiftKey);
			window.removeEventListener('keyup', checkShiftKey);
			window.removeEventListener('blur', unsetShiftKey);
			window.removeEventListener('contextmenu', unsetShiftKey);
			if (playingTimeout.current) clearTimeout(playingTimeout.current);
		};
	}, []);

	useEffect(() => {
		// when returning to the edit state while a shot is still playing, clear the playing state
		// and reset the scroll position
		if (roomState !== ROOM_STATES.SHOWTIME && shotStartTimestampEntity) {
			dispatch(ShotsActions.setPlayingShot(null));
		}

		if (!shotStartTimestampEntity) {
			if (upcomingString) setUpcomingString(durationString(0));
			if (playingTimeout.current) clearTimeout(playingTimeout.current);
		}

		if (roomState === ROOM_STATES.SHOWTIME
			&& (shotStartTimestampEntity !== prevShotStart || isScrollingPinned)
			&& !shiftKeyDepressed
		) {
			handleAutoScroll();
		}

		// when changing room state, reset the scroll positions
		if (roomState !== prevRoomState) {
			setScrollPercent(0);
			setIsScrollingPinned(roomState === ROOM_STATES.SHOWTIME);
			shotRowRef.current?.scrollTo({ left: 0, behavior: 'auto' });
		}
	}, [roomState, shotStartTimestampEntity]);

	const videoDurationRemaining = useMemo(() => Math.ceil(timeLeft), [timeLeft]);

	useEffect(() => {
		if (autoDuration && isVideoSet) {
			updateCoundownUI(videoDurationRemaining);
		} else if (shotDuration) {
			const newStartTimestamp = shotStartTimestampEntity !== prevShotStart;
			const durationUpdated = shotId === prevShotId
				&& (shotDuration !== prevShotDuration || autoDuration !== prevAutoDuration);
			if (newStartTimestamp || durationUpdated) {
				playCountdown();
			}
		} else {
			updateCoundownUI(undefined);
		}
	}, [
		shotId,
		autoDuration,
		shotDuration,
		shotStartTimestampEntity,
		videoDurationRemaining,
		isVideoSet,
	]);

	useEffect(() => {
		if (isScrollingPinned) {
			scrollToPinnedShot(pinnedScrollPos, 'auto');
		} else if (virtuosoRef.current) {
			const indexToScrollTo = Math.floor(scrollPercent * (shots.length - 1));
			virtuosoRef.current.scrollToIndex({
				index: indexToScrollTo,
				behavior: 'auto',
				align: 'start',
				offset: 22,
			});
		}
	}, [isShowboardExpanded]);

	const addShotCallback = useCallback(async (
		layout: string,
		videoCount: number,
		shotPosition: number,
	) => {
		if (!user?.gr_user) return;

		const newShotFeeds = new Array(videoCount).fill(baseShotFeed);
		const newShotData: ShotData = {
			...baseShotData,
			forcedLayout: layout,
			shotFeeds: newShotFeeds,
			order: shotPosition,
		};
		const newShotNoId: Shot = {
			...currentShot,
			shot_data: newShotData,
			shot_id: 0,
			created_by: user?.gr_user,
			edited_by: user?.gr_user,
		};

		const newShot = await handleAddingShot(newShotNoId);

		if (newShot && roomState === ROOM_STATES.EDITING) {
			setShotToSwitchTo(newShot.shot_id);
			dispatch(ShotsActions.setShotTabShotId(newShot.shot_id));
			closeMetaMenuAndDrawer(activeTab);
		}
	}, [handleAddingShot, roomState, currentShot, user]);

	useEffect(() => {
		if (shotToSwitchTo > -1) {
			const newSelectedShot = shots.find((s) => s.shot_id === shotToSwitchTo);
			if (newSelectedShot) {
				handleSelectShot(newSelectedShot.shot_id, true, socket);
				if (!shotTabIsCurrentActiveShot) {
					// if it is the current shot, will switch automatically in redux
					dispatch(ShotsActions.setShotTabShotId(newSelectedShot.shot_id));
				}

				setShotToSwitchTo(-1);
			}
		}
	}, [shots, shotTabIsCurrentActiveShot, shotToSwitchTo]);

	useEffect(() => {
		// When Virtuoso stops scrolling, turn off the flag.
		if (!isVirtuosoScrolling) {
			programmaticScrollRef.current = false;
		}
	}, [isVirtuosoScrolling]);

	const selectShotEditing = useCallback((newShotId: number) => {
		if (roomState === ROOM_STATES.SHOWTIME) return;

		const matchingShot = shots.find((shot: Shot) => shot.shot_id === newShotId);

		if (matchingShot) {
			handleSelectShot(matchingShot.shot_id, true, socket);
			closeMetaMenuAndDrawer(activeTab);
	
			if (!shotTabIsCurrentActiveShot) {
				// if it is the current shot, will switch automatically in redux
				dispatch(ShotsActions.setShotTabShotId(matchingShot.shot_id));
			}
		}
	}, [
		handleSelectShot,
		roomState,
		shots,
		activeTab
	]);

	const selectShotShowtime = useCallback((newShotId: number) => {
		if (roomState === ROOM_STATES.EDITING || !host) return;
		const matchingShot = shots.find((shot: Shot) => shot.shot_id === newShotId);

		if (matchingShot) {
			if (shiftKeyDepressed && currentShotId !== matchingShot.shot_id) {
				setIsScrollingPinned(false);
				track(AmpEvent.shotChangeThumb);
				setNextShotValue(isMovingForward(currentShotId, matchingShot.shot_id, shots));
				// if the current shot has an autoplay timer on, we want to remove it
				if (shotStartTimestampEntity && autoplay && !autoDuration) {
					deleteShotAutoplay(currentShot, true);
				}
				handleSelectShot(matchingShot.shot_id, true, socket);
				if (!shotTabIsCurrentActiveShot) {
					// if it is the current shot, will switch automatically in redux
					dispatch(ShotsActions.setShotTabShotId(matchingShot.shot_id));
				}

				if (clearShot) {
					dispatch(SettingsActions.setClearShot(false));
				}
			} else {
				dispatch(TabsActions.setActiveTab(TABS.SHOTS));
				dispatch(TabsActions.setRightDrawerOpen(true));
				dispatch(ShotsActions.setShotTabShotId(matchingShot.shot_id));
			}
		}
	}, [
		currentShotId,
		handleSelectShot,
		host,
		roomState,
		shiftKeyDepressed,
		shotStartTimestampEntity,
		shots,
		autoplay,
		autoDuration,
	]);

	function isScrolledToEnd(): boolean {
		const virtuosoElement = document.querySelector('.shot-thumbnail-row div[data-testid="virtuoso-scroller"]') as HTMLElement;
		if (!virtuosoElement) return false;

		return virtuosoElement.scrollLeft + virtuosoElement.clientWidth >= virtuosoElement.scrollWidth - 1;
	}

	function scrollWithArrows(right: boolean) {
		if (!virtuosoRef.current || scrollDisabled || isVirtuosoScrolling) return;

		if (isScrollingPinned) setIsScrollingPinned(false);

		const step = 2;
		const current = currentScrollIndex;
		let nextIndex = right
			? Math.min(current + step, shots.length - 1)
			: Math.max(current - step, 0);

		if ((right && isScrolledToEnd())) return;

		programmaticScrollRef.current = true;

		virtuosoRef.current.scrollToIndex({
			index: nextIndex,
			behavior: 'smooth',
			align: 'start',
			offset: 22
		});

		setCurrentScrollIndex(nextIndex);
	}

	const handleDragStart = useCallback((event: DragStartEvent) => {
		if (isScrollingPinned) { setIsScrollingPinned(false); }

		const { active } = event;
		const match = shots.find((shot: Shot) => shot.shot_id === Number(active.id));
		if (match) {
			setIsDragging(true);
			setDraggingShot(match);
		}
	}, [isScrollingPinned, shots]);

	const handleDragOver = useCallback((event: DragOverEvent) => {
		const { over } = event;
		if (over && Number(over.id) === shots[shots.length - 1].shot_id) {
			setStopAutoScroll(true);
		} else {
			setStopAutoScroll(false);
		}
	}, [shots]);

	const handleDragEnd = useCallback((event: DragEndEvent) => {
		if (!token || !meeting) return;
		const { active, over } = event;
		if ((!active || !over) || active.id === over.id) {
			setDraggingShot(null);
		} else if (active.id !== over.id) {
			setDraggingShot(null);
			const oldIndex = shots.findIndex((shot: Shot) => shot.shot_id === Number(active.id));
			const newIndex = shots.findIndex((shot: Shot) => shot.shot_id === Number(over.id));
			const newShots = arrayMove(shots, oldIndex, newIndex)
				.map((shot: Shot, index: number) => {
					/* eslint-disable-next-line no-param-reassign */
					shot.shot_data.order = index;
					return shot;
				});
			// for updating the UI
			dispatch(ShotsActions.updateShotOrder(newShots));
			// for updating the API and sending a socket message
			dispatch(ShotsActions.bulkEditShots(token, newShots));
		}
		setTimeout(() => setIsDragging(false), 300); // for scroll arrow UI
	}, [meeting, shots, token]);

	const lastShotSelected = useMemo(
		() => {
			let isLastShot = true;
			shots.forEach((s) => {
				if (s.shot_data.order > order) {
					isLastShot = false;
				}
			});
			return isLastShot;
		},
		[order, shots.length],
	);

	const resetShotGraphicsState = () => {
		if (token && meeting) {
			resetAllShotOverlays(token, meeting.meeting);
		}

		if (animatedOverlays?.length) {
			if (token && meeting) {
				removeAllAnimatedOverlays(token, meeting.meeting);
			}
		}
	};

	function nextShot(event: React.MouseEvent<HTMLButtonElement>, next: boolean) {
		const currentIndex = shots.findIndex((shot: Shot) => shot.shot_id === shotId);
		let upcomingIndex;

		setNextShotValue(next);

		// if shotStartTimestampEntity is null, the "Start show" button has not been clicked yet
		const isStartingPlayingFirstShotInShow = (!shotStartTimestampEntity && next);
		if (isStartingPlayingFirstShotInShow) {
			track(AmpEvent.shotChangeFirstPlay);
			// if a live show has just begun, this button is used to play the selected shot
			upcomingIndex = currentIndex;
		} else if (currentIndex === 0 && !isStartingPlayingFirstShotInShow && !next) {
			track(AmpEvent.shotChangeShowReset);
			dispatch(ShotsActions.setPlayingShot(null));

			// inform other apps that we are no longer playing a shot
			if (token && meeting) {
				clearShotStartTimestampEntity(token, meeting.meeting);
			}

			upcomingIndex = 0;

			// eslint-disable-next-line @typescript-eslint/no-shadow
			presentedMedia.forEach((media: PresentedMediaItem) => {
				if (media.videoState) {
					const nMedia = {
						...media,
						videoState: {
							...media.videoState,
							paused: true,
							timeElapsed: -1.5,
							volume: media.videoState?.volume ?? 1,
						},
					};
					dispatch(MediaActions.setPresentedMediaVideoState({
						mediaId: nMedia.mediaId,
						videoState: nMedia.videoState,
						doEmitChanges: true,
					}));
				}
			});

			resetShotGraphicsState();

			// Clean up old timer when resetting playing state
			deleteShotAutoplay(currentShot, true);

			return;
		} else {
			track(next ? AmpEvent.shotChangeNext : AmpEvent.shotChangePrev);
			upcomingIndex = next ? currentIndex + 1 : currentIndex - 1;
		}

		appLogger.info('nextShot', {
			currentShotId,
			next,
			upcomingIndex,
			isStartingPlayingFirstShotInShow,
		});

		// if there is a shot to put in the canvas
		if (upcomingIndex > -1) {
			// if the current shot has an autoplay timer on, we want to remove it
			if (shotStartTimestampEntity && autoplay && !autoDuration) {
				deleteShotAutoplay(currentShot, true);
			}

			// handle shot hooks
			handleSelectShot(shots[upcomingIndex].shot_id, true, socket);

			if (!shotTabIsCurrentActiveShot) {
				// if it is the current shot, will switch automatically in redux
				dispatch(ShotsActions.setShotTabShotId(shots[upcomingIndex].shot_id));
			}

			if (clearShot) {
				dispatch(SettingsActions.setClearShot(false));
			}
		}

		event.currentTarget.blur();
	}

	const toggleClearShot = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.currentTarget.blur();

		batch(() => {
			const newPresentedMedia = presentedMedia
				.filter((presentedMediaItem) => presentedMediaItem.videoState)
				.map((presentedMediaItem) => {
					// eslint-disable-next-line no-param-reassign
					presentedMediaItem.videoState!.paused = !clearShot;

					return {
						mediaId: presentedMediaItem.mediaId,
						videoState: presentedMediaItem.videoState!,
						doEmitChanges: true,
					};
				});

			newPresentedMedia.forEach((presentedMediaItem) => {
				dispatch(MediaActions.setPresentedMediaVideoState(presentedMediaItem));
			});

			dispatch(SettingsActions.setClearShot(!clearShot));

			if (clearShot) {
				let shotTimeLeft = shotDuration;

				if (autoDuration) {
					const autoDurationMediaId = getPresentedAutoDurationMediaId(
						currentShot,
						media,
						presentedMedia,
					);

					if (autoDurationMediaId) {
						shotTimeLeft = getPresentedAutoDurationTimeLeft(
							autoDurationMediaId,
							currentShot,
							presentedMedia,
						);
					}
				}

				// Add timer when un-clearing shot
				// (will be deleted in Beehive when shot cleared)
				setShotAutoplay(
					currentShot,
					shotTimeLeft,
				);

				// If shotStartTimestampEntity is null it means it's the first shot
				if (!shotStartTimestampEntity) {
					// Play the next shot (first shot)
					nextShot(e, true);
				}
			}
		});
	};

	const openAddShotModal = (e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
		if (!meeting || !user) return;

		e?.currentTarget?.blur();

		if (roomState === ROOM_STATES.EDITING) {
			setAddShotModalOpen(true);
			return;
		}

		const positionOfNewShot = shotData
			? order + 1
			: shots.length;

		const newShot = {
			...baseShot,
			meeting: meeting.meeting,
			created_by: user.gr_user,
			shot_style: { ...shots[0].shot_style },
			shot_data: {
				...baseShot.shot_data,
				order: positionOfNewShot,
				shotTitle: '',
			},
		};

		handleOpenShotModal(newShot, socket);
	};

	const handleBetweenShotClick = (
		e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
		index: number,
	) => (
		addShotCallback(V_LAYOUTS.ONE.FULL, 1, index)
	);

	function getPlayNextShotButtonText(isAutoplaying: boolean) {
		if (hoveringOnPlayButton && isAutoplaying) {
			return 'Cancel';
		}

		if (shotStartTimestampEntity) {
			return autoplay ? 'Autoplaying' : 'Next Shot';
		}
		return 'Play Shot';
	}

	function handleCancelAutoPlay() {
		if (token) {
			prevAutoplayRef.current = { shotId: currentShot.shot_id, autoplay: currentShot.shot_data.autoplay };
			deleteShotAutoplay(currentShot);
			const { shot_id: shotId } = currentShot
			socket?.sendMessage('temp-auto-play', { shotId });
		}
	}

	const shotIsAutoplaying = useMemo(() => {
		if (shotStartTimestampEntity && autoplay) {
			if (timeLeft && autoDuration) {
				return true;
			}
			const secondsPassed = (new Date().valueOf() - shotStartTimestampEntity.valueOf()) / 1000;
			const secondsRemaining = shotDuration - secondsPassed;

			if (secondsRemaining < 0) {
				return false;
			}
			return true;
		}
		return false;
	}, [shotStartTimestampEntity, autoplay, shotDuration, timeLeft]);

	// Add a sentinel shot to the end of the showboard to render the add shot button
	const showboardShots: Shot[] = useMemo(() => {
		if (host && roomState === ROOM_STATES.EDITING) {
			const sentinel: Shot = {
				...baseShot,
				shot_id: -1,
				shot_data: {
					...baseShot.shot_data,
					shotTitle: '',
				},
			};
			return [...shots, sentinel];
		}
		return shots;
	}, [host, roomState, shots]);


	const lastShotIndex = useMemo(() => shots.length - 1, [shots.length]);

	return (
		<TitlesProvider
			titlesOn={videoTitlesOn}
			shotId={shotId}
		>
			<div
				ref={drawerRef}
				className={classNames('showboard-drawer', {
					open: isShowboardExpanded,
				})}
			>
				<div className="showboard-top-row position-relative">
					<button className="open-button" onClick={() => dispatch(ShowboardActions.setShowboardExpanded(!isShowboardExpanded))}>
						<Icon
							name={isShowboardExpanded ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_UP}
							size={14}
							color={COLORS.WHITE}
						/>
					</button>
					<ShowboardHeader
						isShowboardOpen={isShowboardExpanded}
						pinnedScrollPos={pinnedScrollPos}
						playState={playState}
						upcomingString={upcomingString}
						currentShotScrollPos={isScrollingPinned ? 0 : currentShotScrollPos}
						setScrollingPinned={() => setIsScrollingPinned(true)}
					/>
					{!isDragging && (
						<>
							<button
								className={classNames('scroll-arrow left', roomState === ROOM_STATES.SHOWTIME && 'showtime', host && 'producer')}
								onMouseDown={() => scrollWithArrows(false)}>
								<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={10} color={COLORS.BLACK} />
							</button>
							<button
								className={classNames(
									'scroll-arrow right',
									roomState === ROOM_STATES.SHOWTIME && 'showtime',
									host && 'producer',
								)}
								onMouseDown={() => scrollWithArrows(true)}>
								<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={10} color={COLORS.BLACK} />
							</button>
						</>
					)}
					{host && !isShowboardExpanded && (
						<div className={classNames(
							'top-right-gradient',
							roomState === ROOM_STATES.SHOWTIME && 'showtime',
						)} />
					)}
					{host
						&& <div className="align-items-center position-absolute top-right">
							{roomState === ROOM_STATES.SHOWTIME && (
								<button className={classNames('clear-shot font-weight-heavy padding-small round', clearShot ? 'background-green' : 'background-lighter')} onClick={toggleClearShot}>
									<Icon className="margin-right-small margin-left-small" name={clearShot ? ICONS.LIVE : ICONS.STOP_LIVE} size={12} color={clearShot ? COLORS.BLACK : COLORS.LIGHT_GRAY} />
									<span className={classNames('margin-right-small', clearShot ? 'text-dark' : 'text-light-gray')}>{clearShot ? 'Resume' : 'Clear'}</span>
								</button>
							)}
							{!(roomState === ROOM_STATES.EDITING) && (
								<button className={classNames('add-shot font-weight-heavy padding-small round', roomState === ROOM_STATES.SHOWTIME ? 'showtime background-lighter' : 'background-green')} onClick={openAddShotModal}>
									<Icon className="margin-right-small margin-left-small" name={ICONS.ADD} size={12} color={roomState === ROOM_STATES.SHOWTIME ? COLORS.LIGHT_GRAY : COLORS.BLACK} />
									<span className={classNames('margin-right-small', roomState === ROOM_STATES.SHOWTIME ? 'text-light-gray' : 'text-dark')}>Shot</span>
								</button>
							)}
							{roomState === ROOM_STATES.SHOWTIME && (
								<>
									<button onClick={(e) => nextShot(e, false)} className={classNames('margin-right-small', 'round', 'padding-small', 'background-lighter', !shotStartTimestampEntity && 'disabled')}>
										<Icon name={ICONS.ARROW_LEFT} color={COLORS.WHITE} size={16} />
									</button>
									<TooltipWrapper message={missingFeedsInNextShot}>
										<button
											onClick={(e) => {
												if (shotIsAutoplaying) {
													return handleCancelAutoPlay();
												}
												return nextShot(e, true);
											}}
											className={classNames(
												'round padding-small',
												lastShotSelected && shotStartTimestampEntity && 'disabled',
												missingFeedsInNextShot
													? 'background-warning'
													: (!missingFeedsInNextShot && autoplay && shotStartTimestampEntity
														? 'background-light-medium'
														: 'background-green'
													),
											)}
											onMouseEnter={() => setHoveringOnPlayButton(
												Boolean(true),
											)}
											onMouseLeave={() => setHoveringOnPlayButton(false)}
											ref={playShotRef}
										>
											<span
												className={classNames(
													'margin-right-small font-size-13 slightly-bold margin-left-small play-button-width',
													missingFeedsInNextShot || (autoplay && shotStartTimestampEntity)
														? 'text-white'
														: 'text-dark',
													!autoplay || !shotStartTimestampEntity
														? 'play-button-arrow-width'
														: 'play-button-width',
												)}>
												{getPlayNextShotButtonText(shotIsAutoplaying)}
											</span>
											{(!autoplay || !shotStartTimestampEntity) && (
												<Icon
													className='margin-right-small'
													name={ICONS.ARROW_RIGHT}
													color={missingFeedsInNextShot
														|| (autoplay && shotStartTimestampEntity)
														? COLORS.WHITE
														: COLORS.BLACK
													}
													size={16}
												/>
											)}
										</button>
									</TooltipWrapper>
								</>
							)}
						</div>
					}
				</div>
				<div className="showboard-content" id="showboard">
					<div
						className={classNames(
							'shot-thumbnail-row',
							(roomState === ROOM_STATES.SHOWTIME || !host) && 'showtime',
							host && 'producer',
							isScrollingPinned && 'pinned',
						)}
						onTransitionEnd={(e: any) => {
							if (isScrollingPinned && e.propertyName === 'padding-right') {
								scrollToPinnedShot(pinnedScrollPos, 'smooth');
							}
						}}
						ref={shotRowRef}
						style={{ overflowX: scrollDisabled ? 'hidden' : 'scroll' }}
					>
						<DndContext
							sensors={sensors}
							onDragStart={handleDragStart}
							onDragOver={handleDragOver}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToWindowEdges]}
							autoScroll={!stopAutoScroll}
						>
							<SortableContext
								items={shots.map((shot: Shot) => String(shot.shot_id))}
								strategy={virtualizedHorizontalSortingStrategy}
							>
								<Virtuoso
									ref={virtuosoRef}
									data={showboardShots}
									horizontalDirection
									style={{ width: '100%', height: 200, overflowY: 'hidden', scrollbarWidth: 'none' }}
									isScrolling={setIsVirtuosoScrolling}
									rangeChanged={({ startIndex }) => {
										if (programmaticScrollRef.current) {
											return;
										}
										setCurrentScrollIndex(startIndex)
										const percent = shots.length > 1 ? startIndex / (shots.length - 1) : 0;
										setScrollPercent(percent);
									}}
									itemContent={(index, shot) => {
										if (shot.shot_id === -1) {
											// Render the "Add Shot" button as the last item
											return (
												<div
													className={classNames(
														isShowboardExpanded ? 'add-shot-button' : 'add-shot-button-short',
														isDragging && 'hide-add-shot-button'
													)}
													onClick={(e: React.MouseEvent<HTMLDivElement>) => {
														handleBetweenShotClick(e, shots.length);
													}}
												>
													<Icon color={COLORS.DARK_GRAY_2} name={ICONS.ADD} size={16} />
													{!!isShowboardExpanded && (
														<span style={{ fontSize: '12px !important' }}>Add Shot</span>
													)}
												</div>
											);
										}

										return (
											<div className="showboard-shot-item">
												<ShowboardRowThumbnail
													key={shot.shot_id}
													open={isShowboardExpanded}
													playState={playState}
													beingDragged={draggingShot?.shot_id}
													isScrollingPinned={isScrollingPinned}
													shot={shot}
													onSelect={selectShotEditing}
													onPlay={selectShotShowtime}
													onDragWhilePinned={() => {
														setIsScrollingPinned(false);
														setScrollDisabled(false);
													}}
													shiftKeyDepressed={shiftKeyDepressed}
													drawerRef={drawerRef}
													socket={socket}
												/>
												{!(roomState === ROOM_STATES.EDITING && host)
													? <span style={{ paddingLeft: '19px' }}></span>
													: index === lastShotIndex
														? <></>
														: <BetweenShot
															addSpacingOnHoverLeft={currentShotId === shots?.[index]?.shot_id}
															addSpacingOnHoverRight={
																currentShotId === shots?.[index + 1]?.shot_id
															}
															shotId={shot.shot_id}
															key={`between_${ shot.shot_id }`}
															onClick={(e: React.MouseEvent<HTMLDivElement>) => {
																handleBetweenShotClick(e, index + 1);
															}}
															isShowboardExpanded={isShowboardExpanded}
														/>
												}
											</div>
										);
									}}
								/>
							</SortableContext>
							<DragOverlay>
								{draggingShot
									&& <DraggableShotThumbnail
										shot={draggingShot}
										playStart={shotStartTimestampEntity}
									/>
								}
							</DragOverlay>
						</DndContext>
					</div>
				</div>
			</div>
			<div id="portal-shot-context-menu" />
			{
				addShotModalOpen
				&& <LayoutModal
					open={addShotModalOpen}
					close={() => setAddShotModalOpen(false)}
					onApply={addShotCallback}
				/>
			}
		</TitlesProvider>
	);
}
